import React from 'react'

import SubscriptionManager from '../connection/subscription'
import WSManager from '../connection/websocket'

import { SportsbookError } from '../error'
import SportManager from '../managers/sport'
import { groupBy, minBy, isEmpty } from 'lodash'

import {
  logger,
  getUID,
  updateInArrayById,
  removeFromArrayById,
  verifyCountersBySportServices
} from '../utils'

import {
  SPORTSBOOK_STATE_UPDATE,
  SPORTSBOOK_SPORT_ADD,
  SPORTSBOOK_SPORT_REMOVE,
  SPORTSBOOK_SPORT_UPDATE, SPORTSBOOK_SERVICES_UPDATE, SPORTSBOOK_PATH_UPDATE
} from '../actions'

import { useApplicationState } from './root'
import { useSettings } from './settings'

import { SIDEBAR_ITEMS, SPORT_SERVICE } from '../constants'

const sportsbookReducer = (state, action) => {
  logger.debug(`[sportsbook:${state.id}] dispatching action`, action)

  switch (action.type) {
    case SPORTSBOOK_STATE_UPDATE:
      return {
        ...state,
        ...action.payload
      }
    case SPORTSBOOK_SPORT_ADD:
      return {
        ...state,
        sports: updateInArrayById(
          state.sports,
          action.payload
        ).sort(
          (a, b) => (a.position - b.position)
        )
      }
    case SPORTSBOOK_SPORT_REMOVE:
      return {
        ...state,
        sports: removeFromArrayById(
          state.sports,
          action.payload.id
        )
      }
    case SPORTSBOOK_SPORT_UPDATE:
      return {
        ...state,
        sports: updateInArrayById(
          state.sports,
          action.payload
        )
      }

    case SPORTSBOOK_SERVICES_UPDATE:
      return {
        ...state,
        sportService: action.payload.sportService,
        sportServices: action.payload.sportServices
      }

    case SPORTSBOOK_PATH_UPDATE:
      return {
        ...state,
        selectedSportUrlName: action.payload.selectedSportUrlName,
        selectedCategoryUrlName: action.payload.selectedCategoryUrlName,
        selectedTournamentUrlName: action.payload.selectedTournamentUrlName
      }

    default:
      return state
  }
}

const sportsbookInitialState = {
  initialized: false,
  loading: true,
  error: null,
  sports: []
}

const createSportsbookInitialState = options => {
  const {
    id = getUID(5),

    sportService,
    sportServices,

    selectedSportId = null,
    selectedCategoryId = null,
    selectedTournamentId = null,
    selectedEventId = null,

    selectedSportUrlName = null,
    selectedCategoryUrlName = null,
    selectedTournamentUrlName = null,
    selectedEventUrlName = null,

    fromDate = null,
    toDate = null,

    ...restOptions
  } = options

  const connected = WSManager.connected
  const connectionId = WSManager.connectionId

  return {
    id,
    sportService,
    sportServices,
    connected,
    connectionId,

    ...sportsbookInitialState,

    // and filters
    selectedSportId,
    selectedCategoryId,
    selectedTournamentId,
    selectedEventId,

    selectedSportUrlName,
    selectedCategoryUrlName,
    selectedTournamentUrlName,
    selectedEventUrlName,

    fromDate,
    toDate,
    ...restOptions
  }
}

export const createSportsbookState = options => {
  const [state, dispatcher] = React.useReducer(
    sportsbookReducer,
    (options || {}),
    createSportsbookInitialState
  )

  const { connection } = useApplicationState()
  const { lang } = useSettings()

  /*
    Sportsbook initial sports resolving
  */
  React.useEffect(
    () => {
      // Resetting sportsbook state
      dispatcher({
        type: SPORTSBOOK_STATE_UPDATE,
        payload: {
          loading: true,
          // initialized: false,
          error: null
        }
      })

      // Resolving sportsbook sports
      SportManager.findSports({
        filter: {
          sportService: state.sportService,
          sportServices: state.sportServices,
          fromDate: state.fromDate,
          toDate: state.toDate,
          isOnlyMainMarketTypes: true,
          isOnlyActiveMarkets: true
        }
      })
        .then(res => {
          if (isEmpty(res.data)) {
            throw new SportsbookError('No sports available', res)
          }

          return res.data
        })
        .then(data => {
          const sports = data.sort((a, b) => {
            return (a.position || 0) - (b.position || 0)
          })

          /*
            Resolving sportsbook selected sport initial state partials
            Depending on selected sport id from state - selecting sport
            Currently disabled
          */
          // const initialSelectedSportPartials = getSportsbookInitialSelectedSport(
          //   sports,
          //   state
          // )

          dispatcher({
            type: SPORTSBOOK_STATE_UPDATE,
            payload: {
              initialized: true,
              loading: false,
              sports
              // ...initialSelectedSportPartials
            }
          })
        })
        .catch(error => {
          dispatcher({
            type: SPORTSBOOK_STATE_UPDATE,
            payload: {
              error,
              loading: false,
              initialized: true
            }
          })
        })
    },
    [connection.id, lang, state.fromDate, state.toDate]
  )

  /*
    Sportsbook sports counter subscription handler
  */
  React.useEffect(
    () => {
      const handleSportCounterUpdate = data => {
        const {
          id, // sport id
          prematchCount,
          liveCount
        } = data

        const shouldExist = verifyCountersBySportServices(
          state.sportServices || state.sportService,
          {
            [SPORT_SERVICE.PREMATCH]: prematchCount,
            [SPORT_SERVICE.LIVE]: liveCount
          }
        )

        // If should not exist - removing sport
        if (!shouldExist) {
          return dispatcher({
            type: SPORTSBOOK_SPORT_REMOVE,
            payload: { id }
          })
        }

        const isAlreadyExist = state.sports.find(
          sport => sport.id === id
        )

        // If already exist - updating sport
        if (isAlreadyExist) {
          return dispatcher({
            type: SPORTSBOOK_SPORT_UPDATE,
            payload: {
              id,
              prematchCount,
              liveCount
            }
          })
        }

        // Resolving sport by id and adding to state
        SportManager.findSports({
          filter: {
            sportService: state.sportService,
            sportServices: state.sportServices,
            sportIds: [
              id
            ]
          }
        })
          .then(res => {
            // Validating and extracting sport by id
            if (isEmpty(res.data)) { return }
            const [sport] = res.data
            if (!sport) { return }

            dispatcher({
              type: SPORTSBOOK_SPORT_ADD,
              payload: sport
            })
          })
      }

      // Adding handler for counter updates for all sports
      SportManager.counterBroadcaster.subscribeToSportUpdates(
        -1,
        handleSportCounterUpdate
      )

      return () => {
        SportManager.counterBroadcaster.unsubscribeFromSportUpdates(
          -1,
          handleSportCounterUpdate
        )
      }
    },
    [state.sports]
  )

  /*
    Sportsbook all sports counter subscription
  */
  React.useEffect(
    () => {
      const sportsbookSubscriptionTopics = [
        {
          topic: SubscriptionManager.subscriptionTopics.SPORT_EVENT_COUNT,
          key: -1
        }
      ]

      // Subscribing on notifier
      SubscriptionManager.notifierSubscribe(sportsbookSubscriptionTopics)

      // Binding dispatcher
      const sportsbookDispatcher = action => dispatcher(action)

      // Subscribing dispatcher
      SportManager.sportsbookBroadcaster.subscribe(state.id, sportsbookDispatcher)

      logger.debug(`[sportsbook:${state.id}]`, 'sport state mounted')

      return () => {
        SubscriptionManager.notifierUnsubscribe(sportsbookSubscriptionTopics)

        SportManager.sportsbookBroadcaster.unsubscribe(state.id, sportsbookDispatcher)

        logger.debug(`[sportsbook:${state.id}]`, 'sport state unmounted')
      }
    },
    []
  )

  return [state, dispatcher]
}

export const useSportsbookStateUpdate = () => {
  const [sportsbookState] = useSportsbookState()
  const { id } = sportsbookState

  return React.useCallback(
    payload => (
      SportManager.sportsbookBroadcaster.broadcast(
        id,
        {
          type: SPORTSBOOK_STATE_UPDATE,
          payload
        }
      )
    ),
    []
  )
}

const NO_MARKET_TYPES = {}

export const useSportMainMarketTypes = sportId => {
  const [sportsbookState] = useSportsbookState()
  const { sports } = sportsbookState

  return React.useMemo(
    () => {
      const sportData = sports.find(sport => sport.id === sportId)

      if (!sportData) {
        return NO_MARKET_TYPES
      }

      return Object.values(
        groupBy(
          sportData.mainMarketTypes,
          'periodTypeId'
        )
      )
        .sort((a, b) => {
          return minBy(a, 'mainOrderPosition').mainOrderPosition - minBy(b, 'mainOrderPosition').mainOrderPosition
        })
        .map(periodGroup => (
          periodGroup.sort((a, b) => a.mainOrderPosition - b.mainOrderPosition)
        ))
    },
    [sports, sportId]
  )
}

const SportsbookContext = React.createContext()

export function SportsbookProvider (props) {
  const { value, children } = props

  const [state, dispatcher] = createSportsbookState(value)

  return (
    <SportsbookContext.Provider value={[state, dispatcher]}>
      {children}
    </SportsbookContext.Provider>
  )
}

export const useSportsbookState = () => {
  return React.useContext(SportsbookContext)
}
